import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public page opened from the 20h email link.
 * URL: /justify/:leadId?token=xxx
 * Backend endpoint: POST /api/daily-report/justify/:leadId?token=xxx
 */
export default function JustifyLead() {
  const { leadId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [reason, setReason] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!reason.trim()) {
      toast.error("Descreva a justificação.");
      return;
    }
    if (!token) {
      toast.error("Link inválido — token em falta.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/daily-report/justify/${leadId}?token=${encodeURIComponent(token)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason: reason.trim(),
            action_taken: actionTaken.trim() || null,
          }),
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Erro ao submeter");
      }
      setDone(true);
      toast.success("Justificação registada.");
    } catch (e) {
      toast.error(e.message || "Erro");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 p-6">
        <div className="max-w-md bg-card border border-[hsl(var(--danger))]/25 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl text-foreground">Link inválido</h1>
          <p className="text-sm text-muted-foreground mt-2">
            O link que utilizou não contém um token válido. Verifique o email original.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40 p-6 lg:p-12" data-testid="justify-lead-root">
      <div className="max-w-2xl mx-auto">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-3">
          Relatório Diário · Pedido de Justificação
        </div>
        <h1 className="font-display text-3xl lg:text-4xl text-foreground mb-3">
          Justificar lead
        </h1>
        <p className="text-sm text-muted-foreground mb-8">
          Este lead foi identificado como <b>sem tratamento em tempo útil</b> ou <b>perdido</b> no
          Relatório Diário. Por favor descreva o motivo e (opcionalmente) que ação foi ou vai ser
          tomada.
        </p>

        {done ? (
          <div
            className="bg-card border border-[hsl(var(--success))]/25 rounded-2xl p-8 text-center"
            data-testid="justify-success"
          >
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display text-2xl text-foreground mb-2">Justificação registada</div>
            <p className="text-sm text-muted-foreground">
              Obrigado. A informação ficou guardada no lead e será visível pela direcção.
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-card border border-border rounded-2xl p-6 space-y-5"
          >
            <div>
              <label
                htmlFor="reason"
                className="block text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2"
              >
                Motivo <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason"
                data-testid="justify-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                maxLength={1500}
                className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary resize-y min-h-[140px]"
                placeholder="Ex.: 'Cliente não atendeu em 3 tentativas, ficou combinado retomar 2ª feira 10h.'"
              />
              <div className="text-[10px] text-muted-foreground mt-1 text-right">{reason.length}/1500</div>
            </div>
            <div>
              <label
                htmlFor="action"
                className="block text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-2"
              >
                Acção tomada / a tomar (opcional)
              </label>
              <textarea
                id="action"
                data-testid="justify-action"
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                maxLength={1500}
                className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-primary resize-y min-h-[100px]"
                placeholder="Ex.: 'Agendei nova tentativa para 2ª feira 10h. Adicionei nota no Zoho.'"
              />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || !reason.trim()}
                data-testid="justify-submit"
                className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-40"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Submeter justificação
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
